/**
 * Modern Card Component
 * Clean, spacious card layouts for SaaS UI
 */

import { ReactNode } from 'react'
import styles from './Card.module.css'

type CardProps = {
  children: ReactNode
  className?: string
  variant?: 'default' | 'elevated' | 'outlined' | 'flat'
  padding?: 'none' | 'sm' | 'md' | 'lg'
  hover?: boolean
  onClick?: () => void
  style?: React.CSSProperties
}

export default function Card({
  children,
  className = '',
  variant = 'default',
  padding = 'md',
  hover = false,
  onClick,
  style,
}: CardProps) {
  return (
    <div
      className={`${styles.card} ${styles[variant]} ${styles[`padding-${padding}`]} ${
        hover ? styles.hover : ''
      } ${onClick ? styles.clickable : ''} ${className}`}
      onClick={onClick}
      style={style}
    >
      {children}
    </div>
  )
}

type CardHeaderProps = {
  children: ReactNode
  className?: string
  action?: ReactNode
}

export function CardHeader({ children, className = '', action }: CardHeaderProps) {
  return (
    <div className={`${styles.header} ${className}`}>
      <div className={styles.headerContent}>{children}</div>
      {action && <div className={styles.headerAction}>{action}</div>}
    </div>
  )
}

type CardTitleProps = {
  children: ReactNode
  className?: string
  subtitle?: string
}

export function CardTitle({ children, className = '', subtitle }: CardTitleProps) {
  return (
    <div className={`${styles.title} ${className}`}>
      <h3 className={styles.titleText}>{children}</h3>
      {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
    </div>
  )
}

type CardBodyProps = {
  children: ReactNode
  className?: string
  style?: React.CSSProperties
}

export function CardBody({ children, className = '', style }: CardBodyProps) {
  return <div className={`${styles.body} ${className}`} style={style}>{children}</div>
}

type CardFooterProps = {
  children: ReactNode
  className?: string
}

export function CardFooter({ children, className = '' }: CardFooterProps) {
  return <div className={`${styles.footer} ${className}`}>{children}</div>
}

